import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import graphqlClient from '@/graphql/graphql-client';
import {
  requestOrgContractAnalyses,
  useGetOrgContractAnalyses,
} from '@/graphql/queries/contract-analyses';
import {
  INSERT_ORG_CONTRACT_ANALYSIS_MUTATION,
  UPDATE_ORG_CONTRACT_ANALYSIS_MUTATION,
} from '@/graphql/mutations/contract-analyses';
import type {
  InsertOrgContractAnalysisResponse,
  UpdateOrgContractAnalysisResponse,
  OrgContractAnalysisInsertInput,
  OrgContractAnalysisUpdateInput,
  PaginationParams,
} from '../types/contract-analysis.types';

export const QUERY_KEY = 'contractAnalyses';

const emptyPage = (pageNo: number, pageSize: number) => ({
  items: [],
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  pageSize,
  pageNo,
  totalPages: 0,
});

export const useGetContractAnalyses = (params: PaginationParams) => {
  const query = useGetOrgContractAnalyses({
    ...params,
    order: params.order ?? { direction: 'DESC', field: 'CreatedDate' },
  });
  const pageSize = params.paging?.pageSize ?? 10;
  const pageNo = params.paging?.pageNo ?? 1;
  const result = query.data?.getOrgContractAnalysiss;

  return {
    ...query,
    data: result
      ? {
          items: result.items || [],
          totalCount: result.totalCount || 0,
          hasNextPage: result.hasNextPage || false,
          hasPreviousPage: result.hasPreviousPage || false,
          pageSize: result.pageSize || pageSize,
          pageNo: result.pageNo || pageNo,
          totalPages: result.totalPages || 1,
        }
      : emptyPage(pageNo, pageSize),
  };
};

export const useGetContractAnalysisById = (itemId: string, enabled = true) => {
  return useGlobalQuery({
    queryKey: [QUERY_KEY, 'byId', itemId],
    queryFn: async () => {
      const response = await requestOrgContractAnalyses({
        where: { ItemId: { eq: itemId } },
        paging: { pageNo: 1, pageSize: 1 },
      });
      return response.getOrgContractAnalysiss?.items?.[0] ?? null;
    },
    enabled: Boolean(itemId) && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

export const useCreateContractAnalysis = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async (input: OrgContractAnalysisInsertInput) => {
      const res = await graphqlClient.request(INSERT_ORG_CONTRACT_ANALYSIS_MUTATION, input);
      if (!res.insertOrgContractAnalysis?.itemId) {
        throw new Error('No itemId returned from insert');
      }
      return res as InsertOrgContractAnalysisResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to save analysis' });
    },
  });
};

export const useUpdateContractAnalysis = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async ({
      itemId,
      input,
    }: {
      itemId: string;
      input: OrgContractAnalysisUpdateInput;
    }) => {
      const res = await graphqlClient.request(UPDATE_ORG_CONTRACT_ANALYSIS_MUTATION, {
        ItemId: itemId,
        ...input,
      });
      return res as UpdateOrgContractAnalysisResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleError(error);
    },
  });
};

export const useDeleteContractAnalysis = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await graphqlClient.request(UPDATE_ORG_CONTRACT_ANALYSIS_MUTATION, {
        ItemId: itemId,
        IsInTrash: true,
      });
      return res as UpdateOrgContractAnalysisResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ variant: 'success', title: 'Analysis deleted' });
    },
    onError: (error: Error) => {
      handleError(error);
    },
  });
};
